import { Form, Link, useLocation, useSearchParams } from "react-router-dom";
import Input from "../components/forms/input";
import {
  preventNaNCharactersKeyDown,
  preventNaNCharactersPaste,
} from "../components/utils/preventNaNCharacters";
import { Formik } from "formik";
import { FormEvent, useEffect, useState } from "react";
import { PageError } from "../components/forms/errors/pageError";
import PageErrors from "../components/errors/pageErrors";
import Button from "../components/forms/button";
import Layout from "../components/layout";
import { faEye, faEyeSlash } from "@fortawesome/sharp-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type LoginModel = {
  userName: number | string;
  password: string;
};

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const { state } = useLocation();
  const [pageErrors, setPageErrors] = useState<PageError[]>([]);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [returnUrl, setReturnUrl] = useState<string>()

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>,
    values: LoginModel,
    resetForm: any
  ) {
    event.preventDefault();

    var response: Response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/login`,
      {
        body: JSON.stringify({
          userName: values.userName.toString(),
          password: values.password,
          returnUrl: state?.returnUrl ?? searchParams.get("returnUrl"),
        }),
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
      }
    );

    var result = await response.json();
    if (response.ok) {
      const { returnUrl } = result;
      const { ReturnUrl } = result;
      window.location.href = returnUrl ?? ReturnUrl;
    } else if (response.status >= 400) {
      setPageErrors([...result.page]);
    }
  }

  function scrollButtonIntoView() {
    setTimeout(() => {
      const button = document.getElementById("loginButton");
      button?.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }, 500);
  }

  useEffect(() => {
    setReturnUrl(`${process.env.REACT_APP_API_URL}/account/login?returnUrl=` + (state?.returnUrl ? encodeURIComponent(state.returnUrl) : encodeURIComponent(searchParams.get("returnUrl") ?? "")))
  }, [])

  return (
    <Layout>
      <title>Dayton Freight - Login</title>
      <PageErrors errors={pageErrors} />
      <Formik<LoginModel>
        initialValues={{
          userName: "",
          password: "",
        }}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={() => { }}
      >
        {({ values, resetForm, isSubmitting }) => (
          <Form className="w-full max-w-xl px-5 sm:px-10"
            onSubmit={async (event) =>
              await handleSubmit(event, values, resetForm)
            }
          >
            <div className="flex flex-col gap-5">

              <h1 className={`text-[1.65rem] font-bold text-primary`}>
                Login
              </h1>

              <p className={`text-gray-500`}>
                Don't have an account yet?{" "}
                <Link
                  className="text-primary underline hover:bg-gray-100"
                  to={`${process.env.REACT_APP_API_URL}/account/signup`}
                  state={{
                    returnUrl:
                      returnUrl,
                  }}
                >
                  Sign Up
                </Link>
              </p>

              <Input
                label="Employee Number"
                autoComplete="username"
                inputMode="numeric"
                autoFocus
                type="text"
                id="userName"
                name="userName"
                onFocus={scrollButtonIntoView}
                placeholder="Employee Number"
                tabIndex={1}
                value={values.userName}
                onKeyDown={preventNaNCharactersKeyDown}
                onPaste={preventNaNCharactersPaste}
                required
              />

              <div className="relative">
                <Input
                  label="Password"
                  autoComplete="password"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  tabIndex={2}
                  placeholder="Password"
                  onFocus={scrollButtonIntoView}
                  name="password"
                  required
                />
                <div
                  className={`top-[2.3rem] right-[.75rem] absolute text-gray-300`}
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setShowPassword(!showPassword);
                  }}
                >
                  {" "}
                  {showPassword ? (
                    <FontAwesomeIcon icon={faEyeSlash} />
                  ) : (
                    <FontAwesomeIcon icon={faEye} />
                  )}
                </div>
              </div>

              <Link
                to={`${process.env.REACT_APP_API_URL}/account/reset-password`}
                state={{
                  returnUrl: returnUrl,
                }}
                tabIndex={3}
              >
                Forgot Password?
              </Link>

              <Button
                type="submit"
                id="loginButton"
                disabled={
                  (values.userName && values.password) || isSubmitting
                    ? false
                    : true
                }
                aria-label="Login"
                loading={isSubmitting}
                tabIndex={4}
              >
                Login
              </Button>
            </div>

          </Form>
        )}
      </Formik>
    </Layout>
  );
}
